import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, KeyRound, Loader2, MailCheck, ShieldCheck } from "lucide-react";
import Logo from "@/shared/components/branding/Logo";
import { api, isMockApi } from "@/lib/api";

/**
 * Forgot password, in three steps: email → 6-digit code → new password.
 *
 * Serves vendors and platform admins from one screen. The server's reset flow
 * is role-agnostic — it resolves the account from the address — so splitting
 * this in two would duplicate the whole thing to change one back-link.
 *
 * Two rules the server imposes that this screen has to respect:
 *
 *   1. **Step 1 never reveals whether the address exists.** It answers
 *      identically either way, so this always advances to the code screen. A
 *      "no account with that email" message here would undo the protection.
 *
 *   2. **Step 2 gives one error for every failure.** Wrong code, unknown
 *      address, expired, too many attempts — all `INVALID_RESET_OTP`. The
 *      attempts-remaining count is deliberately not sent, so it cannot be
 *      displayed.
 *
 * See `FORGOT_PASSWORD_CONTRACT.md` for the endpoints themselves.
 */

const STEPS = ["Email", "Code", "New password"];
/** Matches `PASSWORD_RESET_RESEND_COOLDOWN_SECONDS` on the server. */
const RESEND_COOLDOWN_SECONDS = 60;
/** Matches the 8–72 policy in the server's `passwordSchema`. */
const MIN_PASSWORD_LENGTH = 8;

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  // Prefilled when the login screen knows who was trying to sign in.
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const otpInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  /** Counts the resend cooldown down to zero, one second at a time. */
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  /** Move focus to whatever the new step is asking for. */
  useEffect(() => {
    if (step === 1) otpInputRef.current?.focus();
    if (step === 2) passwordInputRef.current?.focus();
  }, [step]);

  /**
   * There is no mock implementation of this flow and there will not be one —
   * resetting a password is a server concern. Saying so beats a screen whose
   * every button fails.
   */
  if (isMockApi) return <OfflineNotice />;

  const requestCode = async (event) => {
    event?.preventDefault();
    setBusy(true);
    try {
      await api.forgotPassword({ email: email.trim() });
      // Advance regardless of whether the address is registered — see rule 1.
      setStep(1);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success("If an account exists for that email, a 6-digit code is on its way.");
    } catch (error) {
      toast.error(error.message || "Could not send the code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try {
      await api.forgotPassword({ email: email.trim() });
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setOtp("");
      // Only the newest code works, so say so — otherwise someone types the
      // first one back in and cannot see why it stopped working.
      toast.success("A new code is on its way. The previous one no longer works.");
    } catch (error) {
      toast.error(error.message || "Could not resend the code.");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (event) => {
    event?.preventDefault();
    setBusy(true);
    try {
      const { resetToken: token } = await api.verifyResetOtp({ email: email.trim(), otp });
      setResetToken(token);
      setStep(2);
    } catch (error) {
      setOtp("");
      otpInputRef.current?.focus();
      toast.error(error.message || "That code is invalid or has expired.");
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (event) => {
    event?.preventDefault();
    setBusy(true);
    try {
      await api.resetPassword({ resetToken, password });
      toast.success("Password updated. Sign in with your new password.");
      // No token comes back by design: the user signs in again, which proves
      // the password is what they think it is.
      nav("/login", { replace: true });
    } catch (error) {
      // The single-use token is spent or expired — there is nothing to retry
      // on this screen, so send them back to the start rather than leave them
      // pressing a button that cannot work.
      if (error.code === "INVALID_RESET_TOKEN") {
        setStep(0);
        setOtp("");
        setResetToken("");
        setPassword("");
      }
      toast.error(error.message || "Could not update the password.");
    } finally {
      setBusy(false);
    }
  };

  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Logo />

          <div className="mt-10 flex items-center gap-2" data-testid="reset-progress">
            {STEPS.map((label, index) => (
              <div
                key={label}
                className={`h-1 flex-1 rounded-full transition-colors ${index <= step ? "bg-brand" : "bg-line"}`}
              />
            ))}
          </div>
          <p className="mt-3 text-xs uppercase tracking-widest text-ink-muted">
            Step {step + 1} of {STEPS.length} · {STEPS[step]}
          </p>

          <AnimatePresence mode="wait">
            {step === 0 && (
              <Step key="step-0">
                <h1 className="mt-4 text-3xl font-display font-medium tracking-tight" data-testid="reset-title">
                  Reset your password
                </h1>
                <p className="mt-2 text-ink-secondary">
                  Enter the email on your account and we'll send a 6-digit code.
                </p>

                <form onSubmit={requestCode} className="mt-8 space-y-4" data-testid="reset-email-form">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor="reset-email">
                      Email
                    </label>
                    <input
                      id="reset-email"
                      type="email"
                      required
                      autoFocus
                      autoComplete="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@business.com"
                      className="input-field mt-2"
                      data-testid="reset-email-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={busy}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="reset-send-code-btn"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send code <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </form>
              </Step>
            )}

            {step === 1 && (
              <Step key="step-1">
                <h1 className="mt-4 text-3xl font-display font-medium tracking-tight" data-testid="reset-title">
                  Enter the code
                </h1>
                <p className="mt-2 text-ink-secondary">
                  We sent a 6-digit code to <span className="text-ink-primary">{email}</span>. It expires in 10 minutes.
                </p>

                <form onSubmit={verifyCode} className="mt-8 space-y-4" data-testid="reset-otp-form">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor="reset-otp">
                      6-digit code
                    </label>
                    <input
                      id="reset-otp"
                      ref={otpInputRef}
                      // `inputMode` gets the numeric keypad on mobile while the
                      // value stays a string — leading zeros are significant.
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="\d{6}"
                      maxLength={6}
                      required
                      value={otp}
                      onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      className="input-field mt-2 text-center text-2xl tracking-[0.5em] font-mono"
                      data-testid="reset-otp-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={busy || otp.length !== 6}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="reset-verify-btn"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify code <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </form>

                <div className="mt-6 flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => setStep(0)}
                    className="text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1.5 transition-colors"
                    data-testid="reset-change-email-btn"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Change email
                  </button>
                  <button
                    type="button"
                    onClick={resendCode}
                    disabled={cooldown > 0 || busy}
                    className="text-brand hover:text-brand-hover disabled:text-ink-muted disabled:cursor-not-allowed transition-colors"
                    data-testid="reset-resend-btn"
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                  </button>
                </div>
              </Step>
            )}

            {step === 2 && (
              <Step key="step-2">
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs text-success">
                  <MailCheck className="h-3.5 w-3.5" /> Code verified
                </div>
                <h1 className="mt-4 text-3xl font-display font-medium tracking-tight" data-testid="reset-title">
                  Choose a new password
                </h1>
                <p className="mt-2 text-ink-secondary">
                  At least {MIN_PASSWORD_LENGTH} characters. This signs you out on every other device.
                </p>

                <form onSubmit={submitPassword} className="mt-8 space-y-4" data-testid="reset-password-form">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor="reset-password">
                      New password
                    </label>
                    <div className="relative mt-2">
                      <input
                        id="reset-password"
                        ref={passwordInputRef}
                        type={showPassword ? "text" : "password"}
                        required
                        minLength={MIN_PASSWORD_LENGTH}
                        maxLength={72}
                        autoComplete="new-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="••••••••"
                        className="input-field pr-11"
                        data-testid="reset-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((shown) => !shown)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center text-ink-muted hover:text-ink-primary transition-colors"
                        aria-label="Toggle password visibility"
                        data-testid="reset-password-toggle"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordTooShort && (
                      <p className="mt-2 text-xs text-danger" data-testid="reset-password-hint">
                        Use at least {MIN_PASSWORD_LENGTH} characters.
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={busy || password.length < MIN_PASSWORD_LENGTH}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="reset-submit-btn"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Update password <Check className="h-4 w-4" /></>}
                  </button>
                </form>
              </Step>
            )}
          </AnimatePresence>

          <p className="mt-8 text-sm text-ink-secondary">
            Remembered it?{" "}
            <Link to="/login" className="text-brand hover:text-brand-hover font-medium transition-colors" data-testid="reset-goto-login">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right visual */}
      <div className="hidden lg:flex relative overflow-hidden border-l border-line items-center justify-center p-12">
        <div className="absolute inset-0 grain" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand/20 via-transparent to-restaurant/10" />
        <div className="relative max-w-md">
          <KeyRound className="h-10 w-10 text-brand" />
          <h2 className="mt-4 text-3xl font-display font-medium tracking-tight">
            Only you can open this door.
          </h2>
          <ul className="mt-8 space-y-3">
            {[
              "The code expires in 10 minutes and works once",
              "Only the newest code is valid — resending retires the last one",
              "Changing your password signs out every other device",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-ink-secondary">
                <span className="mt-1 h-5 w-5 rounded-full bg-success/10 border border-success/30 grid place-items-center flex-shrink-0">
                  <ShieldCheck className="h-3 w-3 text-success" />
                </span>
                {line}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/** One step of the wizard, animated the same way the onboarding wizard is. */
function Step({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Reached under the mock adapter, where there is no server to reset anything
 * on. The login screen hides the link that leads here, so this is only for a
 * direct visit or a stale bookmark.
 */
function OfflineNotice() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Logo />
        <h1 className="mt-10 text-3xl font-display font-medium tracking-tight" data-testid="reset-title">
          Password reset needs the server
        </h1>
        <p className="mt-2 text-ink-secondary">
          The app is running on the mock adapter, where accounts are LocalStorage fixtures and
          there is nothing to email a code to.
        </p>
        <p className="mt-4 text-sm text-ink-muted">
          Set <code className="text-ink-secondary">REACT_APP_USE_MOCK_API=false</code> and{" "}
          <code className="text-ink-secondary">REACT_APP_API_URL</code> in{" "}
          <code className="text-ink-secondary">.env.local</code>, then restart the dev server.
        </p>
        <Link to="/login" className="btn-primary mt-8 inline-flex items-center gap-2" data-testid="reset-goto-login">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
